'use client';

import { notFound, redirect, useParams } from 'next/navigation';
import React from 'react';
import { useWorkspace } from '~/contexts/WorkspaceContext';

// UUID v4 validation regex
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export default function WorkspaceLayout({ children }: { children: React.ReactNode }) {
  const params = useParams();
  const workspaceId = params?.workspaceId as string;
  const { isLoading, hasWorkspaceAccess, defaultWorkspaceId } = useWorkspace();

  // Validate UUID format
  if (!workspaceId || !UUID_REGEX.test(workspaceId)) {
    notFound();
  }

  // Don't gate children on the workspace list query: server-side procedures validate
  // workspace access themselves, and a slow/failed getUserWorkspaces (e.g. a retried
  // transient 401) would otherwise hold the entire page hostage. The access redirect
  // below still runs once the query settles.
  if (!isLoading && !hasWorkspaceAccess) {
    // User doesn't have access to this workspace
    // Redirect to their default workspace
    if (defaultWorkspaceId) {
      // Redirect to default workspace with same path and search params
      const currentPath = window.location.pathname.replace(`/${workspaceId}`, '');
      const searchParams = window.location.search;
      redirect(`/${defaultWorkspaceId}${currentPath}${searchParams}`);
    } else {
      // No workspaces available
      redirect('/no-workspace');
    }
  }

  // WorkspaceProvider is now in the parent (secured) layout
  return <>{children}</>;
}
